import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { branding } from '../constants/branding';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { fetchPayees, addPayee as addPayeeThunk, deletePayee as deletePayeeThunk, Payee } from '../store/slices/payeeSlice';
import { useAppSelector } from '../store/hooks';
import axiosInstance from '../services/axiosConfig';

// Sample payees data
const initialPayees = [
  {
    id: 1,
    name: 'John Doe',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'john.doe@email.com',
    accountNumber: '**** 1234',
    bank: 'Chase Bank',
    lastPayment: 50.00,
    lastPaymentDate: '2024-06-01',
    totalPayments: 12,
    totalAmount: 850.00,
    category: 'Personal',
    isFavorite: true,
  },
  {
    id: 2,
    name: 'Jane Smith',
    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
    email: 'jane.smith@email.com',
    accountNumber: '**** 5678',
    bank: 'Wells Fargo',
    lastPayment: 25.00,
    lastPaymentDate: '2024-05-30',
    totalPayments: 8,
    totalAmount: 320.00,
    category: 'Personal',
    isFavorite: false,
  },
  {
    id: 3,
    name: 'Amazon',
    avatar: 'https://upload.wikimedia.org/wikipedia/commons/a/a9/Amazon_logo.svg',
    email: 'payments@amazon.com',
    accountNumber: '**** 9012',
    bank: 'Amazon Bank',
    lastPayment: 99.99,
    lastPaymentDate: '2024-05-28',
    totalPayments: 45,
    totalAmount: 2847.50,
    category: 'Business',
    isFavorite: true,
  },
  {
    id: 4,
    name: 'Netflix',
    avatar: 'https://upload.wikimedia.org/wikipedia/commons/0/08/Netflix_2015_logo.svg',
    email: 'billing@netflix.com',
    accountNumber: '**** 3456',
    bank: 'Netflix Payments',
    lastPayment: 15.99,
    lastPaymentDate: '2024-05-27',
    totalPayments: 36,
    totalAmount: 575.64,
    category: 'Subscription',
    isFavorite: false,
  },
  {
    id: 5,
    name: 'Sarah Wilson',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    email: 'sarah.wilson@email.com',
    accountNumber: '**** 7890',
    bank: 'Bank of America',
    lastPayment: 75.00,
    lastPaymentDate: '2024-05-25',
    totalPayments: 5,
    totalAmount: 375.00,
    category: 'Personal',
    isFavorite: false,
  },
  {
    id: 6,
    name: 'Spotify',
    avatar: 'https://upload.wikimedia.org/wikipedia/commons/1/19/Spotify_logo_without_text.svg',
    email: 'billing@spotify.com',
    accountNumber: '**** 2345',
    bank: 'Spotify Payments',
    lastPayment: 9.99,
    lastPaymentDate: '2024-05-26',
    totalPayments: 24,
    totalAmount: 239.76,
    category: 'Subscription',
    isFavorite: false,
  },
];

// Payment categories
const categories = ['All', 'Personal', 'Business', 'Subscription'];

const Payees: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [newPayee, setNewPayee] = useState('');
  const [newPayeeEmail, setNewPayeeEmail] = useState('');
  const [newPayeeAccount, setNewPayeeAccount] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddPayee, setShowAddPayee] = useState(false);
  const [sortBy, setSortBy] = useState<'name' | 'lastPayment' | 'totalAmount'>('name');

  // Payment modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayeeForPayment, setSelectedPayeeForPayment] = useState<Payee | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('standard');
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otp, setOtp] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPaymentResultModal, setShowPaymentResultModal] = useState(false);
  const [paymentResult, setPaymentResult] = useState<{ success: boolean; message: string; details?: string } | null>(null);

  const dispatch = useDispatch();
  const { payees, isLoading, error } = useSelector((state: RootState) => state.payees);
  
  // Get profile from Redux for account info
  const profile = useAppSelector((state) => state.profile.profile);

  useEffect(() => {
    dispatch(fetchPayees() as any);
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dispatch]);

  const handleAddPayee = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPayee && newPayeeEmail && newPayeeAccount) {
      dispatch(addPayeeThunk({
        name: newPayee,
        email: newPayeeEmail,
        accountNumber: newPayeeAccount,
        category: 'Personal',
        avatar: `https://i.pravatar.cc/150?u=${newPayeeEmail}`,
        bank: 'Bank',
        lastPayment: 0,
        lastPaymentDate: new Date().toISOString(),
        totalPayments: 0,
        totalAmount: 0,
        isFavorite: false,
      }) as any).then(() => {
        setNewPayee('');
        setNewPayeeEmail('');
        setNewPayeeAccount('');
        setShowAddPayee(false);
      });
    }
  };

  const handleDeletePayee = (id: number) => {
    dispatch(deletePayeeThunk(id) as any);
  };

  // Payment handlers
  const handlePayClick = (payee: Payee) => {
    setSelectedPayeeForPayment(payee);
    setPaymentAmount('');
    setPaymentNote('');
    setPaymentMethod('standard');
    setPaymentError(null);
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setPaymentError('Please enter a valid amount');
      return;
    }
    setShowPaymentModal(false);
    setShowOtpModal(true);
  };

  const handleVerifyOtp = async () => {
    if (!profile?.accountNumber) {
      setPaymentError('Your account information is not loaded. Please try again.');
      return;
    }
    
    if (!selectedPayeeForPayment?.accountNumber) {
      setPaymentError('Please select a valid payee.');
      return;
    }
    
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      setPaymentError('Please enter a valid amount.');
      return;
    }
    
    if (otp.length !== 6) {
      alert('Please enter a 6-digit OTP code.');
      return;
    }
    
    setPaymentLoading(true);
    setPaymentError(null);
    
    try {
      const payload = {
        fromAccountNumber: profile.accountNumber,
        toAccountNumber: selectedPayeeForPayment.accountNumber,
        amount: parseFloat(paymentAmount),
        description: paymentNote || '',
        paymentMethod: paymentMethod || 'standard',
        otpCode: otp
      };
      
      console.log('Sending payment request:', payload);
      
      const response = await axiosInstance.post('/payments', payload);

      if (response.status === 200 && (response.data.success || response.data.status === 'success')) {
        setShowOtpModal(false);
        setPaymentAmount('');
        setPaymentNote('');
        setOtp('');
        setSelectedPayeeForPayment(null);
        setPaymentError(null);
        
        // Show success modal
        setPaymentResult({
          success: true,
          message: 'Payment Successful!',
          details: `Your payment of ${branding.currency?.symbol || '$'}${parseFloat(paymentAmount).toFixed(2)} has been successfully sent to ${selectedPayeeForPayment?.name || 'the recipient'}.`
        });
        setShowPaymentResultModal(true);
        
        // Refresh payees list
        dispatch(fetchPayees() as any);
      } else {
        // Show failure modal
        const errorMessage = response.data.message || 'Payment failed';
        setPaymentResult({
          success: false,
          message: 'Payment Failed',
          details: errorMessage
        });
        setShowPaymentResultModal(true);
        setOtp('');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      console.error('Payment error response:', error.response?.data);
      
      let errorMessage = 'Payment failed. Please try again.';
      let errorDetails = 'An unexpected error occurred while processing your payment.';
      
      if (error.response) {
        const errorData = error.response.data;
        
        if (error.response.status === 401) {
          errorMessage = 'Invalid OTP Code';
          errorDetails = 'The OTP code you entered is invalid or has expired. Please check your Google Authenticator app and try again.';
        } else if (error.response.status === 428) {
          errorMessage = 'OTP Verification Required';
          errorDetails = 'Google Authenticator OTP is required to authorize this payment. Please enter your 6-digit code.';
        } else if (error.response.status === 400) {
          errorMessage = 'Payment Request Failed';
          if (errorData) {
            if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
              errorDetails = errorData.errors.join('. ');
            } else if (errorData.message) {
              errorDetails = errorData.message;
            }
          }
        } else if (error.response.status === 403) {
          errorMessage = 'Access Denied';
          errorDetails = errorData?.message || 'You do not have permission to perform this action.';
        } else if (error.response.status === 404) {
          errorMessage = 'Account Not Found';
          errorDetails = errorData?.message || 'The recipient account could not be found.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server Error';
          errorDetails = 'An internal server error occurred. Please try again later or contact support.';
        } else {
          if (errorData) {
            if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
              errorDetails = errorData.errors.join('. ');
            } else if (errorData.message) {
              errorDetails = errorData.message;
            }
          }
        }
      } else if (error.message) {
        errorDetails = error.message;
      }
      
      // Show failure modal
      setShowOtpModal(false);
      setPaymentResult({
        success: false,
        message: errorMessage,
        details: errorDetails
      });
      setShowPaymentResultModal(true);
      setOtp('');
      setPaymentError(null);
    } finally {
      setPaymentLoading(false);
    }
  };

  // Filter and sort payees
  const filteredPayees = payees
    .filter(payee =>
      (selectedCategory === 'All' || payee.category === selectedCategory) &&
      (payee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payee.email.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'lastPayment':
          return b.lastPayment - a.lastPayment;
        case 'totalAmount':
          return b.totalAmount - a.totalAmount;
        default:
          return 0;
      }
    });

  const totalPayees = payees.length;
  const totalAmount = payees.reduce((sum, payee) => sum + payee.totalAmount, 0);
  const favoritePayees = payees.filter(payee => payee.isFavorite).length;

  return (
    <div style={{
      minHeight: '100vh',
      // background: branding.backgroundGradient,
      fontFamily: branding.fontFamily,
      padding: 0,
      margin: 0
    }}>
      <div style={{
        maxWidth: 1200,
        margin: isMobile ? '0 auto' : '24px auto 0 auto',
        padding: '0 16px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: isMobile ? 24 : 32,
          gap: isMobile ? 16 : 0,
        }}>
          <div>
            <h1 style={{
              color: branding.textColor,
              fontWeight: 700,
              fontSize: isMobile ? 24 : 28,
              margin: 0,
              marginBottom: 8,
            }}>Payees</h1>
            <p style={{
              color: '#666',
              fontSize: isMobile ? 14 : 16,
              margin: 0,
            }}>Manage your saved recipients and payment history</p>
          </div>
          <button
            onClick={() => setShowAddPayee(true)}
            style={{
              padding: isMobile ? '10px 20px' : '12px 24px',
              background: branding.primaryColor,
              color: '#fff',
              border: 'none',
              borderRadius: 12,
              fontSize: isMobile ? 14 : 16,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            <span>➕</span> Add Payee
          </button>
        </div>

        {/* Statistics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: isMobile ? 16 : 24,
          marginBottom: isMobile ? 24 : 32,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>👥</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: branding.textColor, marginBottom: 4 }}>
              {totalPayees}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Total Payees</div>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>⭐</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: branding.textColor, marginBottom: 4 }}>
              {favoritePayees}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Favorites</div>
          </div>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: 24,
            boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>💰</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: branding.textColor, marginBottom: 4 }}>
              ${totalAmount.toFixed(2)}
            </div>
            <div style={{ fontSize: 14, color: '#666' }}>Total Sent</div>
          </div>
        </div>

        {/* Filters and Search */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: isMobile ? '24px 16px' : 32,
          marginBottom: 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          gap: isMobile ? 16 : 24,
          alignItems: 'end',
        }}>
          {/* Search */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: branding.textColor,
            }}>Search Payees</label>
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: '1.5px solid #eee',
                fontSize: 15,
                color: branding.textColor,
                background: '#f7fafd',
                boxSizing: 'border-box',
              }}
            />
          </div>
          {/* Category Filter */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: branding.textColor,
            }}>Category</label>
            <select
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: '1.5px solid #eee',
                fontSize: 15,
                color: branding.textColor,
                background: '#f7fafd',
                appearance: 'none',
              }}
            >
              {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            </select>
          </div>
          {/* Sort By */}
          <div>
            <label style={{
              display: 'block',
              marginBottom: 8,
              fontWeight: 600,
              color: branding.textColor,
            }}>Sort By</label>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value as any)}
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: 12,
                border: '1.5px solid #eee',
                fontSize: 15,
                color: branding.textColor,
                background: '#f7fafd',
                appearance: 'none',
              }}
            >
              <option value="name">Name</option>
              <option value="lastPayment">Last Payment</option>
              <option value="totalAmount">Total Amount</option>
            </select>
          </div>
        </div>

        {/* Payees List */}
        <div style={{
          background: '#fff',
          borderRadius: 20,
          padding: isMobile ? 16 : 32,
          marginBottom: isMobile ? 24 : 32,
          boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
        }}>
          <h3 style={{
            margin: 0,
            marginBottom: isMobile ? 16 : 24,
            color: branding.textColor,
            fontSize: isMobile ? 18 : 20,
            fontWeight: 600,
          }}>Your Payees ({filteredPayees.length})</h3>

          {filteredPayees.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: isMobile ? '32px 16px' : '48px 24px',
              color: '#666',
            }}>
              <div style={{ fontSize: isMobile ? 40 : 48, marginBottom: 16 }}>👥</div>
              <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 600, marginBottom: 8 }}>No payees found</div>
              <div style={{ fontSize: isMobile ? 13 : 14 }}>Try adjusting your search or filters</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: isMobile ? 16 : 20,
            }}>
              {filteredPayees.map(payee => (
                <div key={payee.id} style={{
                  padding: isMobile ? 16 : 20,
                  border: '1.5px solid #eee',
                  borderRadius: 16,
                  background: '#f7fafd',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}>
                  {/* Favorite Button */}
                  <button
                    onClick={() => {/* Navigate to payment page */}}
                    style={{
                      position: 'absolute',
                      top: isMobile ? 12 : 16,
                      right: isMobile ? 12 : 16,
                      background: 'none',
                      border: 'none',
                      fontSize: isMobile ? 18 : 20,
                      cursor: 'pointer',
                      color: payee.isFavorite ? '#ffd700' : '#ccc',
                      transition: 'color 0.2s',
                      padding: 4,
                    }}
                  >
                    {payee.isFavorite ? '⭐' : '☆'}
                  </button>

                  {/* Payee Info */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? 12 : 16,
                    marginBottom: isMobile ? 12 : 16,
                  }}>
                    <img
                      src={payee.avatar}
                      alt={payee.name}
                      style={{
                        width: isMobile ? 48 : 56,
                        height: isMobile ? 48 : 56,
                        borderRadius: '50%',
                        border: '2px solid #fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 600,
                        color: branding.textColor,
                        fontSize: isMobile ? 15 : 16,
                        marginBottom: 4,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{payee.name}</div>
                      <div style={{
                        color: '#666',
                        fontSize: isMobile ? 13 : 14,
                        marginBottom: 2,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{payee.email}</div>
                      <div style={{
                        color: '#888',
                        fontSize: isMobile ? 12 : 13,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>{payee.accountNumber} • {payee.bank}</div>
                    </div>
                  </div>

                  {/* Payment Stats */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr',
                    gap: isMobile ? 12 : 16,
                    marginBottom: isMobile ? 12 : 16,
                    fontSize: isMobile ? 13 : 14,
                  }}>
                    <div>
                      <div style={{ color: '#666', marginBottom: 2 }}>Last Payment</div>
                      <div style={{ 
                        fontWeight: 600, 
                        color: branding.textColor,
                        whiteSpace: 'nowrap',
                      }}>
                        ${payee.lastPayment.toFixed(2)}
                        <span style={{ 
                          color: '#888', 
                          fontSize: isMobile ? 11 : 12, 
                          marginLeft: 4 
                        }}>
                          {payee.lastPaymentDate}
                        </span>
                      </div>
                    </div>
                    <div>
                      <div style={{ color: '#666', marginBottom: 2 }}>Total Sent</div>
                      <div style={{ 
                        fontWeight: 600, 
                        color: branding.primaryColor,
                        whiteSpace: 'nowrap',
                      }}>
                        ${payee.totalAmount.toFixed(2)}
                        <span style={{ 
                          color: '#888', 
                          fontSize: isMobile ? 11 : 12, 
                          marginLeft: 4 
                        }}>
                          {payee.totalPayments} payments
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Category and Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: isMobile ? 8 : 12,
                  }}>
                    <span style={{
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: isMobile ? 11 : 12,
                      fontWeight: 500,
                      background: payee.category === 'Personal' ? '#e3f2fd' : 
                                payee.category === 'Business' ? '#f3e5f5' : '#e8f5e8',
                      color: payee.category === 'Personal' ? '#1976d2' : 
                             payee.category === 'Business' ? '#7b1fa2' : '#388e3c',
                      whiteSpace: 'nowrap',
                    }}>
                      {payee.category}
                    </span>

                    <div style={{
                      display: 'flex',
                      gap: 8,
                      marginLeft: 'auto',
                    }}>
                      <button
                        onClick={() => handlePayClick(payee)}
                        style={{
                          padding: isMobile ? '6px 10px' : '6px 12px',
                          background: branding.primaryColor,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 8,
                          fontSize: isMobile ? 11 : 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Pay
                      </button>
                      <button
                        onClick={() => handleDeletePayee(payee.id)}
                        style={{
                          padding: isMobile ? '6px 10px' : '6px 12px',
                          background: '#fff',
                          color: branding.errorColor,
                          border: '1px solid ' + branding.errorColor,
                          borderRadius: 8,
                          fontSize: isMobile ? 11 : 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add Payee Modal */}
      {showAddPayee && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '16px' : '24px',
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: isMobile ? '24px 20px' : 40,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: 0,
              marginBottom: isMobile ? 20 : 24,
              color: branding.textColor,
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
              textAlign: 'center',
            }}>Add New Payee</h3>

            <form onSubmit={handleAddPayee}>
              <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                  fontSize: isMobile ? 14 : 15,
                }}>Full Name</label>
                <input
                  type="text"
                  value={newPayee}
                  onChange={e => setNewPayee(e.target.value)}
                  placeholder="Enter full name"
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px 14px' : '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: isMobile ? 14 : 15,
                    color: branding.textColor,
                    background: '#f7fafd',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: isMobile ? 16 : 20 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                  fontSize: isMobile ? 14 : 15,
                }}>Email Address</label>
                <input
                  type="email"
                  value={newPayeeEmail}
                  onChange={e => setNewPayeeEmail(e.target.value)}
                  placeholder="Enter email address"
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px 14px' : '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: isMobile ? 14 : 15,
                    color: branding.textColor,
                    background: '#f7fafd',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>

              <div style={{ marginBottom: isMobile ? 24 : 32 }}>
                <label style={{
                  display: 'block',
                  marginBottom: 8,
                  fontWeight: 600,
                  color: branding.textColor,
                  fontSize: isMobile ? 14 : 15,
                }}>Account Number</label>
                <input
                  type="text"
                  value={newPayeeAccount}
                  onChange={e => setNewPayeeAccount(e.target.value)}
                  placeholder="Enter account number"
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px 14px' : '12px 16px',
                    borderRadius: 12,
                    border: '1.5px solid #eee',
                    fontSize: isMobile ? 14 : 15,
                    color: branding.textColor,
                    background: '#f7fafd',
                    boxSizing: 'border-box',
                  }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowAddPayee(false)}
                  style={{
                    flex: 1,
                    padding: isMobile ? '12px 0' : '14px 0',
                    background: '#fff',
                    border: '1.5px solid #eee',
                    borderRadius: 12,
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 600,
                    color: '#666',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: isMobile ? '12px 0' : '14px 0',
                    background: branding.primaryColor,
                    border: 'none',
                    borderRadius: 12,
                    fontSize: isMobile ? 14 : 16,
                    fontWeight: 600,
                    color: '#fff',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 16px rgba(10,132,255,0.2)',
                  }}
                >
                  Add Payee
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedPayeeForPayment && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 20,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: isMobile ? 24 : 32,
            maxWidth: 500,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }}>
            <h3 style={{
              margin: '0 0 20px 0',
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
              color: branding.textColor,
            }}>
              Send Payment
            </h3>

            {paymentError && (
              <div style={{
                padding: 12,
                background: '#fee',
                border: '1px solid #fcc',
                borderRadius: 8,
                marginBottom: 16,
                color: branding.errorColor,
                fontSize: 14,
              }}>
                {paymentError}
              </div>
            )}

            <form onSubmit={handlePaymentSubmit}>
              {/* Recipient Info */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: 8,
                }}>
                  Recipient
                </label>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: 12,
                  background: '#f9f9f9',
                  borderRadius: 8,
                }}>
                  <img
                    src={selectedPayeeForPayment.avatar}
                    alt={selectedPayeeForPayment.name}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover',
                    }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      {selectedPayeeForPayment.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {selectedPayeeForPayment.accountNumber}
                    </div>
                  </div>
                </div>
              </div>

              {/* Amount */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: 8,
                }}>
                  Amount (EUR)
                </label>
                <input
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  step="0.01"
                  min="0.01"
                  required
                  placeholder="0.00"
                  style={{
                    width: '100%',
                    padding: 12,
                    fontSize: 16,
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Note */}
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: 8,
                }}>
                  Note (Optional)
                </label>
                <input
                  type="text"
                  value={paymentNote}
                  onChange={(e) => setPaymentNote(e.target.value)}
                  placeholder="Payment description"
                  style={{
                    width: '100%',
                    padding: 12,
                    fontSize: 14,
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Payment Method */}
              <div style={{ marginBottom: 24 }}>
                <label style={{
                  display: 'block',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#333',
                  marginBottom: 8,
                }}>
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  style={{
                    width: '100%',
                    padding: 12,
                    fontSize: 14,
                    border: '1px solid #ddd',
                    borderRadius: 8,
                    boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="standard">SEPA Standard (Free, 1-2 days)</option>
                  <option value="instant">SEPA Instant (€1.00, Instant)</option>
                  <option value="internal">Internal (Free, Instant)</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPaymentModal(false);
                    setPaymentError(null);
                  }}
                  style={{
                    flex: 1,
                    padding: 12,
                    background: '#fff',
                    border: '1.5px solid #ddd',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#666',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: 12,
                    background: branding.primaryColor,
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#fff',
                    cursor: 'pointer',
                  }}
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* OTP Modal */}
      {showOtpModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1001,
          padding: 20,
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 16,
            padding: isMobile ? 24 : 32,
            maxWidth: 450,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            textAlign: 'center',
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: isMobile ? 20 : 24,
              fontWeight: 700,
              color: branding.textColor,
            }}>
              Google Authenticator Required
            </h3>
            <p style={{
              margin: '0 auto 32px auto',
              color: '#666',
              maxWidth: 400,
              fontSize: 14,
            }}>
              Enter the 6-digit code from your Google Authenticator app to authorize this payment.
            </p>

            <input
              type="tel"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, ''))}
              maxLength={6}
              placeholder="000000"
              style={{
                width: '100%',
                padding: '16px 0',
                fontSize: 32,
                textAlign: 'center',
                letterSpacing: 12,
                border: 'none',
                borderBottom: '2px solid #ddd',
                marginBottom: 24,
                outline: 'none',
                fontWeight: 600,
              }}
            />

            <div style={{ display: 'flex', gap: 12 }}>
              <button
                onClick={() => {
                  setShowOtpModal(false);
                  setOtp('');
                  setPaymentError(null);
                }}
                style={{
                  flex: 1,
                  padding: 12,
                  background: '#fff',
                  border: '1.5px solid #ddd',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#666',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleVerifyOtp}
                disabled={paymentLoading || !profile}
                style={{
                  flex: 1,
                  padding: 12,
                  background: (!profile || paymentLoading) ? '#eee' : branding.primaryColor,
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 14,
                  fontWeight: 600,
                  color: (!profile || paymentLoading) ? '#999' : '#fff',
                  cursor: (!profile || paymentLoading) ? 'not-allowed' : 'pointer',
                }}
              >
                {paymentLoading ? 'Processing...' : 'Verify & Pay'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Result Modal */}
      {showPaymentResultModal && paymentResult && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1003,
          padding: isMobile ? '16px' : '24px',
        }}
        onClick={() => {
          setShowPaymentResultModal(false);
          setPaymentResult(null);
        }}
        >
          <div style={{
            background: '#fff',
            borderRadius: 24,
            padding: isMobile ? '24px 20px' : 40,
            maxWidth: 480,
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Icon */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: 24,
            }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: paymentResult.success 
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 40,
                color: '#fff',
                boxShadow: paymentResult.success
                  ? '0 4px 20px rgba(16, 185, 129, 0.3)'
                  : '0 4px 20px rgba(239, 68, 68, 0.3)',
              }}>
                {paymentResult.success ? '✓' : '✕'}
              </div>
            </div>

            {/* Title */}
            <h3 style={{
              margin: 0,
              marginBottom: 12,
              color: branding.textColor,
              fontSize: isMobile ? 22 : 26,
              fontWeight: 700,
              textAlign: 'center',
            }}>
              {paymentResult.message}
            </h3>

            {/* Details */}
            <p style={{
              margin: 0,
              marginBottom: 32,
              color: '#666',
              fontSize: isMobile ? 14 : 16,
              lineHeight: 1.6,
              textAlign: 'center',
            }}>
              {paymentResult.details}
            </p>

            {/* Action Button */}
            <button
              onClick={() => {
                setShowPaymentResultModal(false);
                setPaymentResult(null);
                if (paymentResult.success) {
                  setShowPaymentModal(false);
                  setSelectedPayeeForPayment(null);
                }
              }}
              style={{
                width: '100%',
                padding: isMobile ? '14px 0' : '16px 0',
                background: paymentResult.success ? branding.successColor : branding.primaryColor,
                border: 'none',
                borderRadius: 12,
                fontSize: isMobile ? 15 : 16,
                fontWeight: 600,
                color: '#fff',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: paymentResult.success
                  ? '0 4px 16px rgba(16, 185, 129, 0.2)'
                  : '0 4px 16px rgba(10, 132, 255, 0.2)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.opacity = '0.9';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.opacity = '1';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              {paymentResult.success ? 'Done' : 'Try Again'}
            </button>
          </div>
        </div>
      )}

      {/* Success Message (kept for backward compatibility) */}
      {showSuccess && (
        <div style={{
          position: 'fixed',
          top: 24,
          right: 24,
          background: branding.successColor,
          color: '#fff',
          padding: '16px 24px',
          borderRadius: 12,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
          zIndex: 1002,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}>
          <span style={{ fontSize: 20 }}>✅</span>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 2 }}>Payment Successful!</div>
            <div style={{ fontSize: 14, opacity: 0.9 }}>Your payment has been processed</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payees; 